// API Service for CSEAN Backend Integration
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.csean.org').replace(/\/$/, '');
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'production';

// Log API configuration in development
if (ENVIRONMENT === 'development') {
  console.log('API Configuration:', {
    baseUrl: API_BASE_URL,
    environment: ENVIRONMENT
  });
}

// Helper function to get API base URL
export const getApiBaseUrl = (): string => {
  return API_BASE_URL;
};

// Helper function to get environment
export const getEnvironment = (): string => {
  return ENVIRONMENT;
};

export interface ApplicationData {
  firstName: string;
  lastName: string;
  middleName?: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  nationality: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode?: string;
  };
  currentPosition: string;
  company: string;
  yearsOfExperience: number;
  education: string;
  certifications?: string[];
  areasOfExpertise?: string[];
  membershipCategory: string;
  motivationForJoining: string;
  previousProfessionalMemberships?: string;
  professionalReferences?: Array<{
    name: string;
    email: string;
    position: string;
    company: string;
  }>;
  agreedToCodeOfEthics: boolean;
  acceptedTermsAndConditions: boolean;
  consentedToDataProcessing: boolean;
}

export interface ApplicationResponse {
  success: boolean;
  applicationId?: string;
  message: string;
  data?: {
    applicationId: string;
    firstName: string;
    lastName: string;
    email: string;
    status: string;
    applicationFee: number;
    createdAt: string;
  };
}

export interface ApplicationStatus {
  success: boolean;
  data?: {
    applicationId: string;
    firstName: string;
    lastName: string;
    email: string;
    status: string;
    applicationFee: number;
    membershipNumber?: string;
    reviewedAt?: string;
    reviewComments?: string;
  };
}

// Submit membership application
export const submitApplication = async (formData: ApplicationData): Promise<ApplicationResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/membership-applications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    const result = await response.json();
    
    if (response.ok && result.success) {
      return result;
    } else {
      throw new Error(result.message || 'Failed to submit application');
    }
  } catch (error) {
    console.error('Application submission error:', error);
    throw error;
  }
};

// Check application status.
//
// The email is sent with the ID because application IDs run in a sequence: without it,
// anyone could count through the IDs and read other people's applications.
export const checkApplicationStatus = async (
  applicationId: string,
  email: string,
): Promise<ApplicationStatus> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/membership-applications/check/${encodeURIComponent(applicationId)}?email=${encodeURIComponent(email)}`,
    );
    const result = await response.json();
    
    if (response.ok && result.success) {
      return result;
    } else {
      throw new Error(result.message || 'Failed to check application status');
    }
  } catch (error) {
    console.error('Status check error:', error);
    throw error;
  }
};

// Transform form data to API format
export const transformFormDataToAPI = (formData: any): ApplicationData => {
  return {
    firstName: formData.personalInfo.firstName,
    lastName: formData.personalInfo.lastName,
    middleName: formData.personalInfo.middleName || '',
    email: formData.personalInfo.email,
    phoneNumber: `${formData.personalInfo.phoneCountryCode}${formData.personalInfo.phone}`,
    dateOfBirth: formData.personalInfo.dateOfBirth,
    nationality: formData.personalInfo.nationality,
    address: {
      street: formData.personalInfo.address,
      city: formData.personalInfo.city,
      state: formData.personalInfo.state,
      country: formData.personalInfo.country,
      postalCode: formData.personalInfo.postalCode || ''
    },
    currentPosition: formData.professionalInfo.currentPosition,
    company: formData.professionalInfo.company,
    yearsOfExperience: formData.professionalInfo.yearsOfExperience,
    education: formData.professionalInfo.education,
    certifications: formData.professionalInfo.certifications || [],
    areasOfExpertise: formData.professionalInfo.areasOfExpertise || [],
    membershipCategory: formData.membershipDetails.selectedCategory,
    motivationForJoining: formData.membershipDetails.motivation || '',
    previousProfessionalMemberships: formData.membershipDetails.previousMemberships || '',
    professionalReferences: formData.membershipDetails.references || [],
    agreedToCodeOfEthics: formData.agreement?.codeOfEthicsAccepted || false,
    acceptedTermsAndConditions: formData.agreement?.termsAccepted || false,
    consentedToDataProcessing: formData.agreement?.dataProcessingAccepted || false
  };
};

// Get status message for display
export const getStatusMessage = (status: string): string => {
  switch (status) {
    case 'pending_review':
      return 'Your application is under review. You will receive an email notification once it has been processed.';
    case 'approved':
      return 'Congratulations! Your application has been approved. Please check your email for login credentials.';
    case 'rejected':
      return 'Your application has been rejected. Please check your email for details.';
    case 'pending_payment':
      return 'Your application has been approved. Please log in to the member portal to complete payment.';
    case 'completed':
      return 'Your application is complete and your account is now active. Welcome to CSEAN!';
    default:
      return 'Unknown status';
  }
};

// Contact Form Types
export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface ContactFormResponse {
  success: boolean;
  message: string;
}

// Submit contact form
export const submitContactForm = async (formData: ContactFormData): Promise<ContactFormResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    const result = await response.json();
    
    if (response.ok && result.success) {
      return result;
    } else {
      throw new Error(result.message || 'Failed to send message');
    }
  } catch (error) {
    console.error('Contact form submission error:', error);
    throw error;
  }
};
